import { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, Plus, X, Settings, Calendar, Info, Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { PricingRule, PricingCondition, OTAChannel, SelectedVariant } from '@/types/revenue-manager';
import { useRoomTypes } from '@/hooks/use-room-inventory';
import { useEvents } from '@/hooks/use-events';
import { transformPricingRuleForAPI, ApiPricingRule } from '@/utils/pricing-rule-transform';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

interface CreatePricingRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (rule: ApiPricingRule) => void; // Now accepts API format
  otaChannels: OTAChannel[];
  basePrice: number;
  editingRule?: PricingRule | null;
  branchId?: string | number;
  orgId?: string | number;
  propertyId?: string | number;
  api_key?: string | number;
  isLoading?: boolean;
}

const conditionTypes = [
  { value: 'daysUntilCheckIn', label: 'Days Before' },
  { value: 'occupancy', label: 'Occupancy %' },
  { value: 'dayOfWeek', label: 'Day of Week' },
  { value: 'event', label: 'Event' },
];

const operators = [
  { value: 'equals', label: 'Equals' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'greaterThanOrEqual', label: '>=' },
  { value: 'lessThanOrEqual', label: '<=' },
];

const frequencies = [
  { value: 'manual', label: 'Manual Only' },
  { value: 'immediate', label: 'Run Once Immediately' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (Multiple Days)' },
  { value: 'monthly', label: 'Monthly (Multiple Days)' },
  { value: 'specific', label: 'Specific Date & Time' },
];

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CreatePricingRuleDialog({
  open,
  onOpenChange,
  onSave,
  otaChannels,
  basePrice,
  editingRule,
  branchId,
  orgId,
  propertyId,
  api_key,
  isLoading = false,
}: CreatePricingRuleDialogProps) {
  const { format: formatCurrency, symbol } = useCurrencyFormat();
  const { data: roomTypesData, isLoading: isRoomTypesLoading, refetch: refetchRoomTypes } = useRoomTypes(propertyId || branchId);
  const { data: eventsData } = useEvents(String(branchId || '')); // Fetch events for current property
  const events = eventsData?.data || [];

  useEffect(() => {
    if (!open) return;
    if (!(propertyId || branchId)) return;
    refetchRoomTypes();
  }, [open, branchId, refetchRoomTypes]);

  // Transform room types from booking scheduler API
  const roomTypesWithVariants = useMemo(() => {
    if (!roomTypesData) return [];
    return roomTypesData.map(item => ({
      id: item.id.toString(),
      name: `${item.room_type} (${item.HotelBranchData.branch_name})`,
      adults: item.num_of_adult_max,
      children: item.num_of_child_max,
      basePrice: item.basic_room_rate,
      roomCount: item?.HotelRoomData?.length || item.room_count,
      variants: (item.HotelRoomTypeVarientRelData || []).map(v => ({
        id: v.id.toString(),
        name: v.VarientRoomTypeDataRel?.name || 'Unknown Variant',
        code: v.variant_wise_code,
        basePrice: v.computed_base_price || v.VarientRoomTypeDataRel?.base_price || '0',
        maxAdults: v.VarientRoomTypeDataRel?.max_adults || 0,
        maxChildren: v.VarientRoomTypeDataRel?.max_children || 0,
      }))
    }));
  }, [roomTypesData]);
  // console.log("roomTypesWithVariants", roomTypesWithVariants)
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(1);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>([]);
  const [selectedOTAChannels, setSelectedOTAChannels] = useState<string[]>([]);
  const [applicateCmNames, setApplicateCmNames] = useState<string[]>([]);
  const [conditions, setConditions] = useState<PricingCondition[]>([
    { type: 'daysUntilCheckIn', operator: 'equals', value: 0 },
  ]);
  const [adjustmentType, setAdjustmentType] = useState<'percentage' | 'fixed'>('percentage');
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [applyTo, setApplyTo] = useState<'basePrice' | 'currentPrice'>('basePrice');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [timeOfDay, setTimeOfDay] = useState('all');
  const [processingFrequency, setProcessingFrequency] = useState<PricingRule['processingFrequency']>('manual');
  const [executionDays, setExecutionDays] = useState<number[]>([]);
  const [executionTime, setExecutionTime] = useState('00:00');
  const [executionStartDate, setExecutionStartDate] = useState<Date | undefined>(undefined);
  const [executionEndDate, setExecutionEndDate] = useState<Date | undefined>(undefined);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Debug: Log otaChannels prop
  // useEffect(() => {
  //   console.log('OTA Channels available in dialog:', otaChannels);
  // }, [otaChannels]);

  useEffect(() => {
    if (editingRule) {
      setName(editingRule.name);
      setPriority(editingRule.priority);
      setSelectedRoomTypes(editingRule.roomTypes);
      
      // Enrich selectedVariants with roomTypeId if missing
      const enrichedVariants = (editingRule.selectedVariants || []).map((variant) => {
        // If variant already has roomTypeId, return as is
        if (variant.roomTypeId && variant.roomTypeId !== '') {
          return variant;
        }
        
        // Otherwise, find the roomTypeId from roomTypesData
        if (roomTypesData) {
          for (const roomType of roomTypesData) {
            const foundVariant = roomType.HotelRoomTypeVarientRelData?.find(
              (v: any) => String(v.id) === String(variant.variantId)
            );
            if (foundVariant) {
              return {
                ...variant,
                roomTypeId: String(roomType.id),
                variantCode: variant.variantCode || foundVariant.variant_wise_code || `V${variant.variantId}`,
                variantName: variant.variantName || foundVariant.VarientRoomTypeDataRel?.name || 'Unknown Variant'
              };
            }
          }
        }
        return variant;
      });
      
      setSelectedVariants(enrichedVariants);
      setSelectedOTAChannels(editingRule.otaChannels || []);
      setApplicateCmNames((editingRule as any).applicateCmNames || []);
      
      // Debug log for event conditions
      console.log('Setting conditions for editing rule:', editingRule.conditions);
      editingRule.conditions.forEach((c, idx) => {
        if (c.type === 'event') {
          console.log(`Event condition ${idx}:`, {
            value: c.value,
            eventDetails: c.eventDetails
          });
        }
      });
      
      setConditions(editingRule.conditions);
      setAdjustmentType(editingRule.adjustmentType);
      setAdjustmentValue(editingRule.adjustmentValue);
      setApplyTo(editingRule.applyTo || 'basePrice');
      setStartDate(new Date(editingRule.startDate));
      setEndDate(editingRule.endDate ? new Date(editingRule.endDate) : undefined);
      setSelectedDays(editingRule.daysOfWeek || []);
      setTimeOfDay(editingRule.timeOfDay || 'all');
      setProcessingFrequency(editingRule.processingFrequency);
      setExecutionDays(editingRule.executionDays || []);
      setExecutionTime(editingRule.executionTime || '00:00');
      setExecutionStartDate((editingRule as any).executionStartDate ? new Date((editingRule as any).executionStartDate) : undefined);
      setExecutionEndDate(editingRule.executionEndDate ? new Date(editingRule.executionEndDate) : undefined);
    } else {
      // Reset form
      setName('');
      setPriority(1);
      setSelectedRoomTypes([]);
      setSelectedVariants([]);
      setSelectedOTAChannels([]);
      setApplicateCmNames([]);
      setConditions([{ type: 'daysUntilCheckIn', operator: 'equals', value: 0 }]);
      setAdjustmentType('percentage');
      setAdjustmentValue(0);
      setApplyTo('basePrice');
      setStartDate(new Date());
      setEndDate(undefined);
      setSelectedDays([]);
      setTimeOfDay('all');
      setProcessingFrequency('manual');
      setExecutionDays([]);
      setExecutionTime('00:00');
      setExecutionStartDate(undefined);
      setExecutionEndDate(undefined);
    }
  }, [editingRule, open, roomTypesData]);

  const finalPrice = adjustmentType === 'percentage'
    ? basePrice * (1 + adjustmentValue / 100)
    : basePrice + adjustmentValue;

  const addCondition = (logic: 'AND' | 'OR' = 'AND') => {
    // Set the logic on the previous condition
    if (conditions.length > 0) {
      const updated = [...conditions];
      updated[updated.length - 1] = { ...updated[updated.length - 1], logic };
      setConditions([...updated, { type: 'daysUntilCheckIn', operator: 'equals', value: 0 }]);
    } else {
      setConditions([{ type: 'daysUntilCheckIn', operator: 'equals', value: 0 }]);
    }
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, field: keyof PricingCondition, value: any) => {
    const updated = [...conditions];
    
    // If changing to event type, populate event details
    if (field === 'value' && updated[index].type === 'event') {
      const selectedEvent = events.find(e => e.id === value);
      if (selectedEvent) {
        updated[index] = {
          ...updated[index],
          value,
          eventDetails: {
            eventId: selectedEvent.id,
            eventName: selectedEvent.name,
            startDate: selectedEvent.startDate,
            endDate: selectedEvent.endDate,
          }
        };
        setConditions(updated);
        return;
      }
    }
    
    updated[index] = { ...updated[index], [field]: value };
    setConditions(updated);
  };

  const toggleDay = (dayIndex: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const toggleExecutionDay = (day: number) => {
    setExecutionDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleRoomType = (roomTypeId: string) => {
    const isCurrentlySelected = selectedRoomTypes.includes(roomTypeId);
    
    if (isCurrentlySelected) {
      // When deselecting room type, also remove its variants
      setSelectedRoomTypes((prev) => prev.filter((id) => id !== roomTypeId));
      setSelectedVariants((variants) => variants.filter((v) => v.roomTypeId !== roomTypeId));
    } else {
      // When selecting room type, also select all its variants
      setSelectedRoomTypes((prev) => [...prev, roomTypeId]);
      const roomType = roomTypesWithVariants.find(rt => rt.id === roomTypeId);
      if (roomType && roomType.variants.length > 0) {
        const newVariants = roomType.variants.map(v => ({
          roomTypeId: roomTypeId,
          variantId: v.id,
          variantCode: v.code,
          variantName: v.name
        }));
        setSelectedVariants((prev) => [...prev, ...newVariants]);
      }
    }
  };

  const selectAllRoomTypes = () => {
    const allRoomTypeIds = roomTypesWithVariants.map(rt => rt.id);
    const allSelected = selectedRoomTypes.length === roomTypesWithVariants.length;
    
    if (allSelected) {
      // Deselect all room types and clear variants
      setSelectedRoomTypes([]);
      setSelectedVariants([]);
    } else {
      // Select all room types and all their variants
      setSelectedRoomTypes(allRoomTypeIds);
      const allVariants: SelectedVariant[] = [];
      roomTypesWithVariants.forEach(roomType => {
        if (roomType.variants.length > 0) {
          roomType.variants.forEach(variant => {
            allVariants.push({
              roomTypeId: roomType.id,
              variantId: variant.id,
              variantCode: variant.code,
              variantName: variant.name
            });
          });
        }
      });
      setSelectedVariants(allVariants);
    }
  };

  const toggleVariant = (roomTypeId: string, variantId: string, variantCode: string, variantName: string) => {
    setSelectedVariants((prev) => {
      const exists = prev.some((v) => v.roomTypeId === roomTypeId && v.variantId === variantId);
      if (exists) {
        const newVariants = prev.filter((v) => !(v.roomTypeId === roomTypeId && v.variantId === variantId));
        // If no variants left for this room type, uncheck the room type as well
        const hasVariantsForRoomType = newVariants.some(v => v.roomTypeId === roomTypeId);
        if (!hasVariantsForRoomType) {
          setSelectedRoomTypes(prevRoomTypes => prevRoomTypes.filter(id => id !== roomTypeId));
        }
        return newVariants;
      }
      return [...prev, { roomTypeId, variantId, variantCode, variantName }];
    });
  };

  const isVariantSelected = (roomTypeId: string, variantId: string) => {
    return selectedVariants.some((v) => v.roomTypeId === roomTypeId && v.variantId === variantId);
  };

  const getVariantsForRoomType = (roomTypeId: string) => {
    return selectedVariants.filter((v) => v.roomTypeId === roomTypeId);
  };

  const selectAllVariantsForRoomType = (roomTypeId: string) => {
    const roomType = roomTypesWithVariants.find(rt => rt.id === roomTypeId);
    if (!roomType) return;
    
    const currentlySelected = getVariantsForRoomType(roomTypeId);
    const allSelected = currentlySelected.length === roomType.variants.length;
    
    if (allSelected) {
      // Deselect all variants for this room type
      setSelectedVariants(prev => prev.filter(v => v.roomTypeId !== roomTypeId));
    } else {
      // Select all variants for this room type
      const newVariants: SelectedVariant[] = roomType.variants.map(v => ({
        roomTypeId,
        variantId: v.id,
        variantCode: v.code,
        variantName: v.name,
      }));
      setSelectedVariants(prev => [
        ...prev.filter(v => v.roomTypeId !== roomTypeId),
        ...newVariants,
      ]);
    }
  };

  const toggleOTAChannel = (code: string) => {
    setSelectedOTAChannels((prev) => {
      const newChannels = prev.includes(code)
        ? prev.filter((id) => id !== code)
        : [...prev, code];
      console.log('Toggle OTA Channel - Previous:', prev, 'New:', newChannels);
      return newChannels;
    });
  };

  const getSelectedDayNames = () => {
    return selectedDays.map((i) => daysOfWeek[i]).join(', ');
  };

  const validateRule = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate name
    if (!name.trim()) {
      errors.name = 'Rule name is required';
    } else if (name.trim().length < 3) {
      errors.name = 'Rule name must be at least 3 characters';
    }

    // Validate room types
    if (selectedRoomTypes.length === 0) {
      errors.roomTypes = 'Please select at least one room type';
    }

    // OTA channels are optional - no validation needed

    // Validate conditions
    if (conditions.length === 0) {
      errors.conditions = 'At least one condition is required';
    } else {
      // Check each condition has a valid value
      const hasInvalidCondition = conditions.some(condition => {
        if (condition.type === 'event') {
          return !condition.value || (!condition.eventDetails && !events.find(e => e.id === condition.value));
        }
        return condition.value === undefined || condition.value === null || condition.value === '';
      });
      
      if (hasInvalidCondition) {
        errors.conditions = 'All conditions must have valid values';
      }
    }

    // Validate adjustment value
    if (!adjustmentValue && adjustmentValue !== 0) {
      errors.adjustmentValue = 'Adjustment value is required';
    } else if (adjustmentValue === 0) {
      errors.adjustmentValue = 'Adjustment value cannot be 0';
    } else if (adjustmentType === 'percentage' && Math.abs(adjustmentValue) > 100) {
      errors.adjustmentValue = 'Percentage adjustment cannot exceed ±100%';
    } else if (adjustmentValue > 0 && adjustmentType === 'fixed' && adjustmentValue > 100000) {
      errors.adjustmentValue = 'Fixed adjustment seems unusually high';
    }

    // Validate dates - Start date is optional when condition is "Days Before"
    const hasDaysBeforeCondition = conditions.some(c => c.type === 'daysUntilCheckIn');
    if (!hasDaysBeforeCondition && !startDate) {
      errors.startDate = 'Start date is required (or use "Days Before" condition)';
    }

    if (endDate && endDate < startDate) {
      errors.endDate = 'End date must be after start date';
    }

    // Validate processing frequency requirements
    if (processingFrequency === 'weekly' && executionDays.length === 0) {
      errors.executionDays = 'Please select at least one day for weekly execution';
    }

    if (processingFrequency === 'monthly' && executionDays.length === 0) {
      errors.executionDays = 'Please select at least one day for monthly execution';
    }

    if (processingFrequency === 'specific' && !executionTime) {
      errors.executionTime = 'Execution time is required for specific scheduling';
    }

    if (['hourly', 'daily', 'weekly', 'monthly'].includes(processingFrequency)) {
      if (!executionStartDate) {
        errors.executionStartDate = 'Execution Start Date is required';
      }
      if (executionEndDate && executionStartDate && executionEndDate < executionStartDate) {
        errors.executionEndDate = 'Execution End Date must be after Execution Start Date';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== FORM SUBMISSION ===');
    console.log('selectedOTAChannels state:', selectedOTAChannels);
    console.log('selectedOTAChannels length:', selectedOTAChannels.length);
    
    // Validate before saving
    if (!validateRule()) {
      // Scroll to first error
      const firstErrorElement = document.querySelector('[data-error="true"]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Build the dialog format rule
    const dialogRule: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'> & { applicateCmNames?: string[] } = {
      name,
      priority,
      isActive: true,
      conditions,
      adjustmentType,
      adjustmentValue,
      applyTo,
      roomTypes: selectedRoomTypes,
      selectedVariants: selectedVariants.length > 0 ? selectedVariants : undefined,
      otaChannels: selectedOTAChannels,
      applicateCmNames: applicateCmNames,
      startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
      endDate: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : undefined,
      daysOfWeek: selectedDays.length > 0 ? selectedDays : undefined,
      timeOfDay: timeOfDay !== 'all' ? timeOfDay : undefined,
      processingFrequency,
      executionDays: executionDays.length > 0 ? executionDays : undefined,
      executionTime: processingFrequency !== 'manual' ? executionTime : undefined,
      executionStartDate: executionStartDate ? new Date(Date.UTC(executionStartDate.getFullYear(), executionStartDate.getMonth(), executionStartDate.getDate())).toISOString() : undefined,
      executionEndDate: executionEndDate ? new Date(Date.UTC(executionEndDate.getFullYear(), executionEndDate.getMonth(), executionEndDate.getDate())).toISOString() : undefined,
    };

    console.log('Selected OTA Channels:', selectedOTAChannels);
    console.log('Dialog Rule:', dialogRule);

    // Transform to API format (branchId is used as property_id in API)
    const apiRule = transformPricingRuleForAPI(
      dialogRule,
      orgId || '0',
      propertyId || '0',
        branchId || 0,
        api_key || ''
    );

    console.log('API Rule:', apiRule);
    console.log('API Rule otaChannels:', apiRule.otaChannels);

    // Add id to the API rule if editing
    if (editingRule?.id) {
      apiRule.id = editingRule.id;
    }

    onSave(apiRule);
    // Don't close here - let parent handle closing on success
  };
  console.log('api_key=========:', api_key);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingRule && editingRule.id ? 'Edit Pricing Rule' : 'Create New Pricing Rule'}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Define conditions and price adjustments for dynamic pricing
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <DialogBody className="space-y-6 max-h-[400px] overflow-y-auto">
          {/* Validation Error Summary */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4" data-error="true">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-destructive mb-2">Please fix the following errors:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                    {Object.values(validationErrors).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ruleName" className={validationErrors.name ? 'text-destructive' : ''}>
                  Rule Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ruleName"
                placeholder="e.g., Last Minute Surge"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (validationErrors.name) {
                    const { name: _, ...rest } = validationErrors;
                    setValidationErrors(rest);
                  }
                }}
                className={validationErrors.name ? 'border-destructive' : ''}
                data-error={!!validationErrors.name}
                required
              />
              {validationErrors.name && (
                <p className="text-sm text-destructive">{validationErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority <span className="text-destructive">*</span></Label>
              <Input
                id="priority"
                type="number"
                min={1}
                value={priority}
                onChange={(e) => {
                  const value = e.target.value === '' ? 1 : Number(e.target.value);
                  setPriority(value);
                }}
              />
            </div>
          </div>

          <Separator />

          {/* Target Room Types */}
          <div className="space-y-3" data-error={!!validationErrors.roomTypes}>
            <div className="flex items-center justify-between">
              <div>
                <Label className={cn("text-base font-semibold", validationErrors.roomTypes && "text-destructive")}>
                    Target Room Types <span className="text-destructive">*</span>
                </Label>
                <p className="text-sm text-muted-foreground">
                  Select specific room types for this rule.
                </p>
              </div>
              {roomTypesWithVariants?.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={selectAllRoomTypes}
                >
                  {(selectedRoomTypes?.length || 0) === (roomTypesWithVariants?.length || 0) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>
            {validationErrors.roomTypes && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.roomTypes}
              </p>
            )}
            
            {/* Selected chips */}
            {(selectedRoomTypes?.length || 0) > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-3">
                <span className="text-sm font-medium text-accent">
                  Selected ({selectedRoomTypes?.length || 0}):
                </span>
                {selectedRoomTypes?.map((id) => {
                  const room = roomTypesWithVariants?.find((r) => r.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="gap-1 pl-2">
                      {room?.name}
                      <button
                        type="button"
                        onClick={() => toggleRoomType(id)}
                        className="ml-1 rounded-full hover:bg-background/50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            {isRoomTypesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="rounded-lg border border-border p-4 space-y-3 max-h-64 overflow-y-auto">
                {roomTypesWithVariants?.map((roomType) => (
                  <div key={roomType.id} className="space-y-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={roomType.id}
                        checked={selectedRoomTypes?.includes(roomType.id) || false}
                        onCheckedChange={() => toggleRoomType(roomType.id)}
                        className="mt-0.5"
                      />
                      <label htmlFor={roomType.id} className="flex-1 cursor-pointer">
                        <span className="font-medium">{roomType.name}</span>
                        <p className="text-sm text-muted-foreground">
                          {roomType.adults}A / {roomType.children}C · {formatCurrency(Number(roomType.basePrice))} · {roomType.roomCount} rooms
                        </p>
                      </label>
                    </div>
                    {/* Show variants when room type is selected */}
                    {selectedRoomTypes.includes(roomType.id) && roomType.variants.length > 0 && (
                      <div className="ml-8 pl-4 border-l-2 border-accent/30 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Select Variants ({getVariantsForRoomType(roomType.id).length}/{roomType.variants.length})
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs px-2"
                              onClick={() => selectAllVariantsForRoomType(roomType.id)}
                            >
                              {getVariantsForRoomType(roomType.id).length === roomType.variants.length ? 'Deselect All' : 'Select All'}
                            </Button>
                            {getVariantsForRoomType(roomType.id).length === 0 && (
                              <span className="text-xs text-muted-foreground italic">All included if none selected</span>
                            )}
                          </div>
                        </div>
                        {roomType.variants.map((variant) => {
                          const variantKey = `${roomType.id}-${variant.id}`;
                          const isSelected = isVariantSelected(roomType.id, variant.id);
                          return (
                            <label
                              key={variantKey}
                              htmlFor={variantKey}
                              className={cn(
                                "flex items-center gap-2 py-1.5 px-3 rounded-md cursor-pointer transition-colors",
                                isSelected 
                                  ? "bg-accent/20 border border-accent/40" 
                                  : "bg-muted/50 hover:bg-muted"
                              )}
                            >
                              <Checkbox
                                id={variantKey}
                                checked={isSelected}
                                onCheckedChange={() => toggleVariant(roomType.id, variant.id, variant.code, variant.name)}
                              />
                              <Badge variant="outline" className="text-xs font-mono">
                                {variant.code}
                              </Badge>
                              <span className="text-sm font-medium">{variant.name}</span>
                              <span className="text-xs text-muted-foreground ml-auto">
                                {formatCurrency(Number(variant.basePrice))} · {variant.maxAdults}A/{variant.maxChildren}C
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Target OTA Channels - Hide when api_key is available */}
          {!api_key && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              Target OTA Channels
            </Label>
            <p className="text-sm text-muted-foreground">
              Select specific booking channels for this rule. Leave empty to apply to all channels.
            </p>

            {/* Selected chips */}
            {selectedOTAChannels.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-3">
                <span className="text-sm font-medium text-accent">
                  Selected ({selectedOTAChannels.length}):
                </span>
                {selectedOTAChannels.map((id) => {
                  const channel = otaChannels.find((c) => c.code === id);
                  return (
                    <Badge key={id} variant="secondary" className="gap-1 pl-2">
                      {channel?.name}
                      <button
                        type="button"
                        onClick={() => toggleOTAChannel(channel?.code)}
                        className="ml-1 rounded-full hover:bg-background/50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <div className="rounded-lg border border-border p-4 space-y-3 max-h-48 overflow-y-auto">
              {otaChannels.map((channel) => (
                <div key={channel.id} className="flex items-start gap-3">
                  <Checkbox
                    id={channel.id}
                    checked={selectedOTAChannels.includes(channel.code)}
                    onCheckedChange={() => toggleOTAChannel(channel.code)}
                    className="mt-0.5"
                  />
                  <label htmlFor={channel.id} className="flex-1 cursor-pointer">
                    <span className="font-medium">{channel.name}</span>
                    <p className="text-sm text-muted-foreground">
                      {channel.commission > 0 && `${channel.commission}% commission${channel.description ? ' · ' : ''}`}
                      {channel.description}
                    </p>
                  </label>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Channel Manager Integration */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              Channel Manager Integration
            </Label>
            <p className="text-sm text-muted-foreground">
              Select channel managers to sync this rule with.
            </p>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="channex-cm"
                  checked={applicateCmNames.includes('Channex')}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setApplicateCmNames([...applicateCmNames, 'Channex']);
                    } else {
                      setApplicateCmNames(applicateCmNames.filter(name => name !== 'Channex'));
                    }
                  }}
                  className="mt-0.5"
                />
                <label htmlFor="channex-cm" className="flex-1 cursor-pointer">
                  <span className="font-medium">Channex</span>
                  <p className="text-sm text-muted-foreground">
                    Sync this rule with Channex channel manager
                  </p>
                </label>
              </div>
            </div>
          </div>

          <Separator />

          {/* Conditions */}
          <div className="space-y-3" data-error={!!validationErrors.conditions}>
            <div className="flex items-center justify-between">
              <Label className={cn("text-base font-semibold", validationErrors.conditions && "text-destructive")}>
                  Conditions <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addCondition('AND')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add AND
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addCondition('OR')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add OR
                </Button>
              </div>
            </div>
            {validationErrors.conditions && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {validationErrors.conditions}
              </p>
            )}
            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div key={index}>
                  {index > 0 && (
                    <div className="flex justify-center py-2">
                      <Select
                        value={conditions[index - 1].logic || 'AND'}
                        onValueChange={(value: 'AND' | 'OR') => updateCondition(index - 1, 'logic', value)}
                      >
                        <SelectTrigger className="w-20 h-7 text-xs font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">AND</SelectItem>
                          <SelectItem value="OR">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex flex-wrap items-start gap-3 rounded-lg border border-border p-3">
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-muted-foreground">Condition Type</Label>
                      <Select
                        value={condition.type}
                        onValueChange={(value: PricingCondition['type']) => {
                          // Update all fields at once to avoid state conflicts
                          const updated = [...conditions];
                          if (value === 'event') {
                            updated[index] = { 
                              ...updated[index], 
                              type: value, 
                              value: events[0]?.id || '', 
                              operator: 'equals' 
                            };
                          } else if (value === 'dayOfWeek') {
                            updated[index] = { 
                              ...updated[index], 
                              type: value, 
                              value: 0, 
                              operator: 'equals' 
                            };
                          } else if (value === 'daysUntilCheckIn') {
                            updated[index] = { 
                              ...updated[index], 
                              type: value, 
                              value: 0, 
                              operator: 'equals' 
                            };
                          } else {
                            updated[index] = { 
                              ...updated[index], 
                              type: value, 
                              value: 0 
                            };
                          }
                          setConditions(updated);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {conditionTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Operator - hide for event, dayOfWeek, and daysUntilCheckIn types since operator is always "equals" */}
                    {condition.type !== 'event' && condition.type !== 'dayOfWeek' && condition.type !== 'daysUntilCheckIn' && (
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-muted-foreground">Operator</Label>
                        <Select
                          value={condition.operator}
                          onValueChange={(value) => updateCondition(index, 'operator', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Operator" />
                          </SelectTrigger>
                          <SelectContent>
                            {operators.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Value - different inputs based on condition type */}
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-muted-foreground">Value</Label>
                      {condition.type === 'event' ? (
                        <Select
                          value={condition.value as string}
                          onValueChange={(value) => updateCondition(index, 'value', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Event" />
                          </SelectTrigger>
                          <SelectContent>
                            {events.length === 0 ? (
                              <SelectItem value="no-events" disabled>No events available</SelectItem>
                            ) : (
                              events.map((event) => (
                                <SelectItem key={event.id} value={event.id}>
                                  {event.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      ) : condition.type === 'dayOfWeek' ? (
                        <Select
                          value={String(condition.value)}
                          onValueChange={(value) => updateCondition(index, 'value', Number(value))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Day" />
                          </SelectTrigger>
                          <SelectContent>
                            {daysOfWeek.map((day, i) => (
                              <SelectItem key={i} value={String(i)}>
                                {day}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type="number"
                          placeholder="Value"
                          value={condition.value as number}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : Number(e.target.value);
                            updateCondition(index, 'value', value);
                          }}
                        />
                      )}
                    </div>
                    
                    {/* Event Details Display - shows when event type is selected */}
                    {condition.type === 'event' && condition.eventDetails && (
                      <div className="w-full mt-2 p-3 bg-muted/50 rounded-lg border border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">🎉</span>
                          <span className="font-medium text-sm">{condition.eventDetails.eventName}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Start Date:</span>{' '}
                            {new Date(condition.eventDetails.startDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                          <div>
                            <span className="font-medium">End Date:</span>{' '}
                            {new Date(condition.eventDetails.endDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Show event details inline even without eventDetails populated yet */}
                    {condition.type === 'event' && !condition.eventDetails && condition.value && (
                      (() => {
                        const selectedEvent = events.find(e => e.id === condition.value);
                        if (!selectedEvent) return null;
                        return (
                          <div className="w-full mt-2 p-3 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">🎉</span>
                              <span className="font-medium text-sm">{selectedEvent.name}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div>
                                <span className="font-medium">Start Date:</span>{' '}
                                {new Date(selectedEvent.startDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </div>
                              <div>
                                <span className="font-medium">End Date:</span>{' '}
                                {new Date(selectedEvent.endDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    )}
                    
                    {conditions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeCondition(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Adjustment */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adjustment Type <span className="text-destructive">*</span></Label>
                <Select value={adjustmentType} onValueChange={(v) => setAdjustmentType(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ({symbol})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={validationErrors.adjustmentValue ? 'text-destructive' : ''}>
                    Adjustment Value <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  value={adjustmentValue}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : Number(e.target.value);
                    setAdjustmentValue(value as number);
                    if (validationErrors.adjustmentValue) {
                      const { adjustmentValue: _, ...rest } = validationErrors;
                      setValidationErrors(rest);
                    }
                  }}
                  className={validationErrors.adjustmentValue ? 'border-destructive' : ''}
                  data-error={!!validationErrors.adjustmentValue}
                />
                {validationErrors.adjustmentValue && (
                  <p className="text-sm text-destructive">{validationErrors.adjustmentValue}</p>
                )}
              </div>
            </div>

            {/* Apply To Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-semibold">Apply Adjustment To</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose whether this adjustment applies to the original base price or the current price after previous rules.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setApplyTo('basePrice')}
                  className={cn(
                    'rounded-lg border-2 p-4 text-left transition-all',
                    applyTo === 'basePrice'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/50'
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={cn(
                      'h-4 w-4 rounded-full border-2',
                      applyTo === 'basePrice' ? 'border-primary bg-primary' : 'border-muted-foreground'
                    )}>
                      {applyTo === 'basePrice' && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        </div>
                      )}
                    </div>
                    <span className="font-medium">Base Price</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Always calculate from original base price</p>
                  <p className="text-xs text-primary mt-2">
                    Example: Base {symbol}100 → This Rule → Final Price
                  </p>
                  <p className="text-xs text-muted-foreground">Ignores adjustments from previous rules</p>
                </button>
                <button
                  type="button"
                  onClick={() => setApplyTo('currentPrice')}
                  className={cn(
                    'rounded-lg border-2 p-4 text-left transition-all',
                    applyTo === 'currentPrice'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/50'
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={cn(
                      'h-4 w-4 rounded-full border-2',
                      applyTo === 'currentPrice' ? 'border-primary bg-primary' : 'border-muted-foreground'
                    )}>
                      {applyTo === 'currentPrice' && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        </div>
                      )}
                    </div>
                    <span className="font-medium">Current Price</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Stack on top of previous rule adjustments</p>
                  <p className="text-xs text-primary mt-2">
                    Example: Previous {symbol}120 → This Rule → Final Price
                  </p>
                  <p className="text-xs text-muted-foreground">Compounds with previous rules</p>
                </button>
              </div>
            </div>
          </div>

          {/* Applicability Period */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">Applicability Period</p>
                <p className="text-sm text-muted-foreground">
                  Define when this rule is active and valid
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  Start Date {!conditions.some(c => c.type === 'daysUntilCheckIn') && <span className="text-destructive">*</span>}
                  {conditions.some(c => c.type === 'daysUntilCheckIn') && <span className="text-muted-foreground">(Optional with Days Before)</span>}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>
                  End Date <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'No end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Days of Week <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Select days or leave empty to apply every day
              </p>
              <div className="flex gap-2">
                {daysOfWeek.map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(index)}
                    className={cn('day-selector', selectedDays.includes(index) && 'active')}
                  >
                    {day}
                  </button>
                ))}
              </div>
              {selectedDays.length > 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 mt-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Active on: {getSelectedDayNames()}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Time of Day</Label>
              <Select value={timeOfDay} onValueChange={setTimeOfDay}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Day (24 hours)</SelectItem>
                  <SelectItem value="morning">Morning (6AM - 12PM)</SelectItem>
                  <SelectItem value="afternoon">Afternoon (12PM - 6PM)</SelectItem>
                  <SelectItem value="evening">Evening (6PM - 12AM)</SelectItem>
                  <SelectItem value="night">Night (12AM - 6AM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Execution Schedule */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Settings className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">Execution Schedule</p>
                <p className="text-sm text-muted-foreground">
                  Define when the system processes and applies this rule
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Processing Frequency</Label>
              <Select
                value={processingFrequency}
                onValueChange={(v) => {
                  console.log("Processing Frequency",v,executionStartDate);
                  setProcessingFrequency(v as typeof processingFrequency);
                  if (v === 'daily') {
                    setExecutionStartDate(new Date());
                  }else{
                    setExecutionStartDate(undefined);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frequencies.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {processingFrequency === 'manual' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Manual Execution</p>
                  <p className="text-sm text-muted-foreground">
                    This rule will only run when manually triggered via the "Run Now" button.
                  </p>
                </div>
              </div>
            )}

            {processingFrequency === 'immediate' && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm font-medium">Immediate Execution</p>
                <p className="text-sm text-muted-foreground">
                  This rule will run once immediately after saving.
                </p>
              </div>
            )}

            {processingFrequency === 'hourly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Hourly Execution</p>
                  <p className="text-sm text-muted-foreground">
                    This rule will run automatically every hour.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={(date) => {
                            setExecutionStartDate(date);
                            if (validationErrors.executionStartDate) {
                              const { executionStartDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!validationErrors.executionEndDate}>
                    <Label className={validationErrors.executionEndDate ? 'text-destructive' : ''}>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className={cn(
                              "flex-1 justify-start text-left font-normal",
                              validationErrors.executionEndDate && "border-destructive"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={executionEndDate}
                            onSelect={(date) => {
                              setExecutionEndDate(date);
                              if (validationErrors.executionEndDate) {
                                const { executionEndDate: _, ...rest } = validationErrors;
                                setValidationErrors(rest);
                              }
                            }}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {executionEndDate && (
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setExecutionEndDate(undefined)}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {validationErrors.executionEndDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionEndDate}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'daily' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Daily Execution</p>
                  <p className="text-sm text-muted-foreground">
                    This rule will run once per day at the specified time.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={(date) => {
                            setExecutionStartDate(date);
                            if (validationErrors.executionStartDate) {
                              const { executionStartDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!validationErrors.executionEndDate}>
                    <Label className={validationErrors.executionEndDate ? 'text-destructive' : ''}>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className={cn(
                              "flex-1 justify-start text-left font-normal",
                              validationErrors.executionEndDate && "border-destructive"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={executionEndDate}
                            onSelect={(date) => {
                              setExecutionEndDate(date);
                              if (validationErrors.executionEndDate) {
                                const { executionEndDate: _, ...rest } = validationErrors;
                                setValidationErrors(rest);
                              }
                            }}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {executionEndDate && (
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setExecutionEndDate(undefined)}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {validationErrors.executionEndDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionEndDate}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'weekly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Weekly Execution</span>
                  </div>
                  <Label className="text-sm">Days of week:</Label>
                  <div className="flex gap-2 mt-2">
                    {daysOfWeek.map((day, index) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleExecutionDay(index)}
                        className={cn(
                          'day-selector',
                          executionDays.includes(index) && 'active'
                        )}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={(date) => {
                            setExecutionStartDate(date);
                            if (validationErrors.executionStartDate) {
                              const { executionStartDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!validationErrors.executionEndDate}>
                    <Label className={validationErrors.executionEndDate ? 'text-destructive' : ''}>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionEndDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionEndDate}
                          onSelect={(date) => {
                            setExecutionEndDate(date);
                            if (validationErrors.executionEndDate) {
                              const { executionEndDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionEndDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionEndDate}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'monthly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Monthly Execution</span>
                  </div>
                  <Label className="text-sm">Days of month:</Label>
                  <div className="grid grid-cols-7 gap-1 mt-2">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleExecutionDay(day)}
                        className={cn(
                          'h-9 rounded-md text-sm font-medium transition-colors border',
                          executionDays.includes(day)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border hover:bg-muted'
                        )}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={(date) => {
                            setExecutionStartDate(date);
                            if (validationErrors.executionStartDate) {
                              const { executionStartDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!validationErrors.executionEndDate}>
                    <Label className={validationErrors.executionEndDate ? 'text-destructive' : ''}>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionEndDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionEndDate}
                          onSelect={(date) => {
                            setExecutionEndDate(date);
                            if (validationErrors.executionEndDate) {
                              const { executionEndDate: _, ...rest } = validationErrors;
                              setValidationErrors(rest);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionEndDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionEndDate}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'specific' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Specific Date & Time</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select a specific date and time to run this rule.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Execution Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionDays.length > 0 
                            ? format(new Date(executionDays[0]), 'PPP') 
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionDays.length > 0 ? new Date(executionDays[0]) : undefined}
                          onSelect={(date) => date && setExecutionDays([date.getTime()])}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Execution time:</Label>
                    <Input
                      type="time"
                      value={executionTime}
                      onChange={(e) => setExecutionTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Price Preview - only show when applying to base price */}
          {applyTo === 'basePrice' && (
            <div className="flex items-center justify-between rounded-lg bg-muted p-4">
              <div>
                <p className="text-sm font-medium">Price Preview</p>
                <p className="text-sm text-muted-foreground">
                  Base: {formatCurrency(basePrice)} → Final: {formatCurrency(finalPrice)}
                </p>
              </div>
              <span
                className={cn(
                  'rounded-full px-4 py-2 text-sm font-semibold',
                  adjustmentValue >= 0
                    ? 'bg-status-active-bg text-status-active'
                    : 'bg-red-100 text-red-600'
                )}
              >
                {adjustmentValue >= 0 ? '+' : ''}
                {adjustmentType === 'percentage' ? `${adjustmentValue}%` : `${symbol}${adjustmentValue}`}
              </span>
            </div>
          )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button type="submit" className="bg-status-active hover:bg-status-active/90" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Settings className="mr-2 h-4 w-4" />
                  Save Rule
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
